import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageBackground } from "@/components/ui/page-background";
import { AppHeader } from "@/components/app-header";
import { useAuth } from "@/hooks/use-auth";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Users, Clock, Trophy, Calendar, Music, Eye, ArrowLeft, Percent, BarChart as BarChartIcon, X, UserCheck, Search, RefreshCw, Loader2 } from "lucide-react";

interface AnalyticsOverview {
  totalEvents?: number;
  totalPerformers: number;
  uniquePerformers: number;
  totalPerformances: number;
  avgStageTime: number;
  signupToPerformancePercentage: number;
  avgPerformancesPerActivePerformer: number;
  topItems: Array<{ item: string; count: number }>;
  itemType: 'instruments' | 'performerTypes' | 'none';
  recentActivity?: Array<{
    eventName: string;
    eventDate: string;
    performerCount: number;
    avgStageTime: number;
  }>;
}

interface Event {
  id: number;
  name: string;
  date: string;
  type: string;
  venue: string;
}

interface PerformerStats {
  mostActivePerformers: Array<{
    participantId: number;
    participantName: string;
    totalPerformances: number;
    totalStageTime: number;
    eventsAttended: number;
  }>;
  longestStageTime: Array<{
    participantId: number;
    participantName: string;
    totalStageTime: number;
    avgStageTime: number;
    totalPerformances: number;
  }>;
  mostFrequentPerformers: Array<{
    participantId: number;
    participantName: string;
    eventsAttended: number;
    totalPerformances: number;
  }>;
}


export default function AnalyticsDashboard() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { organisationId } = useParams<{ organisationId: string }>();
  const [, setLocation] = useLocation();
  
  // Get URL parameters for initial state
  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = urlParams.get('tab') || 'overview';
  const initialEventType = urlParams.get('eventType') || null;
  const initialEventId = urlParams.get('eventId') ? parseInt(urlParams.get('eventId')!) : null;
  const initialSearch = urlParams.get('search') || '';
  
  const [selectedEventId, setSelectedEventId] = useState<number | null>(initialEventId);
  const [selectedEventType, setSelectedEventType] = useState<string | null>(initialEventType);
  const [performerSearchTerm, setPerformerSearchTerm] = useState<string>(initialSearch);
  
  const currentOrgId = organisationId ? parseInt(organisationId) : null;
  
  // Get current organization info
  const currentOrg = user?.organisationUsers?.find(ou => ou.organisation.id === currentOrgId)?.organisation;
  
  // Check if user has access to this organization
  const hasAccess = user?.role === 'god' || user?.organisationUsers?.some(ou => 
    ou.organisation.id === currentOrgId && (ou.role === 'owner' || ou.role === 'operator')
  );

  // Show loading state while auth is loading
  if (isAuthLoading) {
    return (
      <PageBackground>
        <AppHeader username={user?.username} />
        <div className="flex items-center justify-center relative" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center relative z-10">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-slate-600 drop-shadow-sm">Loading analytics...</p>
          </div>
        </div>
      </PageBackground>
    );
  }

  if (!hasAccess) {
    return (
      <PageBackground>
        <AppHeader username={user?.username} />
        <div className="flex items-center justify-center relative" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center relative z-10">
            <h2 className="text-xl font-semibold mb-2 text-slate-900 drop-shadow-sm">Access Denied</h2>
            <p className="text-slate-600 mb-4 drop-shadow-sm">You don't have permission to view analytics for this organization.</p>
            <Button onClick={() => setLocation('/')} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </PageBackground>
    );
  }

  // Fetch event types for dropdown
  const { data: eventTypes, refetch: refetchEventTypes } = useQuery<string[]>({
    queryKey: [`/api/stats/event-types?organisationId=${currentOrgId}`],
    enabled: !!currentOrgId && hasAccess,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });

  // Auto-select event type if organization only has one type
  useEffect(() => {
    if (eventTypes && eventTypes.length === 1 && !selectedEventType) {
      setSelectedEventType(eventTypes[0]);
    }
  }, [eventTypes, selectedEventType]);

  // Fetch events list for dropdown (filtered by event type if selected)
  const eventsQuery = selectedEventType 
    ? `/api/stats/events?organisationId=${currentOrgId}&eventType=${selectedEventType}`
    : `/api/stats/events?organisationId=${currentOrgId}`;
    
  const { data: events, refetch: refetchEvents } = useQuery<Event[]>({
    queryKey: [eventsQuery],
    enabled: !!currentOrgId && hasAccess,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });

  // Fetch analytics data
  let analyticsQuery = `/api/stats/overview?organisationId=${currentOrgId}`;
  if (selectedEventId) {
    analyticsQuery += `&eventId=${selectedEventId}`;
  }
  if (selectedEventType) {
    analyticsQuery += `&eventType=${selectedEventType}`;
  }
    
  const { data: overview, isLoading: overviewLoading, error: overviewError, refetch: refetchOverview, isFetching: overviewFetching } = useQuery<AnalyticsOverview>({
    queryKey: [analyticsQuery],
    enabled: !!currentOrgId && hasAccess,
    staleTime: 2 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });


  let performerStatsQuery = `/api/stats/performer-stats?organisationId=${currentOrgId}&limit=10`;
  if (selectedEventId) {
    performerStatsQuery += `&eventId=${selectedEventId}`;
  }
  if (selectedEventType) {
    performerStatsQuery += `&eventType=${selectedEventType}`;
  }

  const { data: performerStats, isLoading: performerStatsLoading, error: performerStatsError, refetch: refetchPerformerStats, isFetching: performerStatsFetching } = useQuery<PerformerStats>({
    queryKey: [performerStatsQuery],
    enabled: !!currentOrgId && hasAccess,
    staleTime: 2 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });



  if (!user || !currentOrgId) {
    return (
      <PageBackground>
        <AppHeader username={user?.username} />
        <div className="flex items-center justify-center relative" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center relative z-10">
            <h2 className="text-xl font-semibold mb-2 text-slate-900 drop-shadow-sm">Invalid Organization</h2>
            <Button onClick={() => setLocation('/')} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </PageBackground>
    );
  }


  const formatMinutes = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <PageBackground>
      <AppHeader username={user?.username} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 relative z-10">
        <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between -mb-2">
          <div className="flex items-center justify-between md:justify-start gap-4 mt-2">
            <div className="flex items-center gap-4">

              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold drop-shadow-sm">
                  Analytics
                </h1>
                {(overviewFetching || performerStatsFetching) && (
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                )}
              </div>
            </div>
            
            {/* Refresh button on same row as title on mobile */}
            <div className="flex items-center gap-2 md:hidden">
              <Button
                onClick={() => {
                  refetchOverview();
                  refetchPerformerStats();
                  refetchEvents();
                  refetchEventTypes();
                }}
                variant="outline"
                size="sm"
                disabled={overviewFetching || performerStatsFetching}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${(overviewFetching || performerStatsFetching) ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
          
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:gap-3">
            <div className="flex flex-col gap-2 md:flex-row md:gap-2">
              <div className="md:w-48">
                <Select value={selectedEventType || 'none'} onValueChange={(value) => setSelectedEventType(value === 'none' ? null : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">All event types</SelectItem>
                    {eventTypes?.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type === 'jam' ? 'Jam Nights' : type === 'openmic' ? 'Open Mic' : 'Generic'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedEventType && (
                <div className="md:w-48">
                  <Select 
                    value={selectedEventId?.toString() || 'all'} 
                    onValueChange={(value) => setSelectedEventId(value === 'all' ? null : parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All events" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All events</SelectItem>
                      {events?.map((event) => (
                        <SelectItem key={event.id} value={event.id.toString()}>
                          {event.name} - {new Date(event.date).toLocaleDateString()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              {(selectedEventType || selectedEventId) && (
                <Button 
                  onClick={() => {
                    setSelectedEventType(null);
                    setSelectedEventId(null);
                    setPerformerSearchTerm("");
                    // Clear URL parameters
                    window.history.replaceState({}, '', `/analytics/${organisationId}`);
                  }} 
                  variant="outline" 
                  size="sm"
                  className="w-full md:w-auto"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              )}
              
              {/* Refresh button with filters on desktop */}
              <Button
                onClick={() => {
                  refetchOverview();
                  refetchPerformerStats();
                  refetchEvents();
                  refetchEventTypes();
                }}
                variant="outline"
                size="sm"
                disabled={overviewFetching || performerStatsFetching}
                className="hidden md:flex"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${(overviewFetching || performerStatsFetching) ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>


        <Tabs defaultValue={initialTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-2 lg:w-80">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="performers">Performers</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Show loading state */}
            {overviewLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="ml-2 text-slate-600">Loading analytics data...</span>
              </div>
            )}
            
            {/* Key Metrics Cards */}
            {!overviewLoading && (
            <div className={`grid gap-6 ${selectedEventId ? 'md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6'}`}>
              {!selectedEventId && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Events</CardTitle>
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{overview?.totalEvents || 0}</div>
                    <p className="text-xs text-muted-foreground">All-time events hosted</p>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Signups</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{overview?.totalPerformers || 0}</div>
                  <p className="text-xs text-muted-foreground">Unique participants</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Performed</CardTitle>
                  <UserCheck className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{overview?.uniquePerformers || 0}</div>
                  <p className="text-xs text-muted-foreground">Total number of people who've performed at least once</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Performance Rate</CardTitle>
                  <Percent className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{overview?.signupToPerformancePercentage || 0}%</div>
                  <p className="text-xs text-muted-foreground">Signups who performed</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Performances</CardTitle>
                  <Trophy className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{overview?.totalPerformances || 0}</div>
                  <p className="text-xs text-muted-foreground">Number of times people have performed in total</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Stage Time</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatMinutes(overview?.avgStageTime || 0)}</div>
                  <p className="text-xs text-muted-foreground">Per performer</p>
                </CardContent>
              </Card>



              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Performances</CardTitle>
                  <BarChartIcon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{overview?.avgPerformancesPerActivePerformer?.toFixed(1) || 0}</div>
                  <p className="text-xs text-muted-foreground">Per active performer</p>
                </CardContent>
              </Card>
            </div>
            )}

            {/* Charts */}
            {!overviewLoading && (
            <div className="grid gap-6 md:grid-cols-2">
              {overview?.itemType !== 'none' && overview?.topItems && overview.topItems.length > 0 && (
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Music className="h-5 w-5" />
                      {overview?.itemType === 'performerTypes' ? 'All Performer Types' : 'All Instruments Selected'}
                    </CardTitle>
                    <CardDescription>
                      {overview?.itemType === 'performerTypes' 
                        ? 'Complete breakdown of performer types across events' 
                        : 'Complete breakdown of instruments selected across events'
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart 
                        data={overview.topItems}
                        margin={{ top: 20, right: 5, left: 5, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="item" 
                          angle={-45}
                          textAnchor="end"
                          height={60}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis 
                          allowDecimals={false} 
                          domain={[0, 'dataMax']} 
                          width={15}
                          ticks={Array.from({length: Math.max(...overview.topItems.map(item => item.count)) + 1}, (_, i) => i)}
                        />
                        <Tooltip 
                          formatter={(value) => [value, 'Times Selected']}
                          labelStyle={{ color: '#000' }}
                          contentStyle={{ 
                            backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                            border: '1px solid #ccc',
                            borderRadius: '4px'
                          }}
                        />
                        <Bar dataKey="count" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {!selectedEventId && (
                <Card className={overview?.itemType !== 'none' && overview?.topItems && overview.topItems.length > 0 ? 'md:col-span-2' : ''}>
                  <CardHeader>
                    <CardTitle>Recent Activity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {overview?.recentActivity?.map((event, index) => (
                        <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-white/10">
                          <div>
                            <p className="font-medium">{event.eventName}</p>
                            <p className="text-sm text-muted-foreground">{formatDate(event.eventDate)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm">{event.performerCount} performers</p>
                            <p className="text-xs text-muted-foreground">
                              {formatMinutes(Math.round(event.avgStageTime || 0))} avg
                            </p>
                          </div>
                        </div>
                      )) || (
                        <div className="text-center text-muted-foreground py-4">No recent activity</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            )}
          </TabsContent>

          <TabsContent value="performers" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Most Active Performers</CardTitle>
                <CardDescription>Ranked by total number of performances</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      type="text"
                      placeholder="Search performers..."
                      className="pl-10"
                      value={performerSearchTerm}
                      onChange={(e) => setPerformerSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  {performerStats?.mostActivePerformers
                    ?.filter(performer => 
                      performer.participantName.toLowerCase().includes(performerSearchTerm.toLowerCase())
                    )
                    ?.map((performer, index) => (
                    <div key={performer.participantId} className="p-4 rounded-lg bg-white/10">
                      {/* Desktop layout */}
                      <div className="hidden md:flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge variant="secondary" className="w-8 h-8 rounded-full flex items-center justify-center">
                            {index + 1}
                          </Badge>
                          <div>
                            <Link href={`/analytics/${currentOrgId}/performer/${performer.participantId}?organisationId=${currentOrgId}&tab=performers&eventType=${selectedEventType || ''}&eventId=${selectedEventId || ''}&search=${performerSearchTerm}`}>
                              <p className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer">
                                {performer.participantName}
                              </p>
                            </Link>
                            <p className="text-sm text-muted-foreground">
                              {performer.eventsAttended} events attended
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-semibold">{performer.totalPerformances} performances</p>
                            <p className="text-sm text-muted-foreground">
                              {formatMinutes(performer.totalStageTime)} total stage time
                            </p>
                          </div>
                          <Link href={`/analytics/${currentOrgId}/performer/${performer.participantId}?organisationId=${currentOrgId}&tab=performers&eventType=${selectedEventType || ''}&eventId=${selectedEventId || ''}&search=${performerSearchTerm}`}>
                            <Button variant="outline" size="sm">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Link>
                        </div>
                      </div>

                      {/* Mobile layout */}
                      <div className="md:hidden space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge variant="secondary" className="w-8 h-8 rounded-full flex items-center justify-center">
                              {index + 1}
                            </Badge>
                            <div>
                              <Link href={`/analytics/${currentOrgId}/performer/${performer.participantId}?organisationId=${currentOrgId}&tab=performers&eventType=${selectedEventType || ''}&eventId=${selectedEventId || ''}&search=${performerSearchTerm}`}>
                                <p className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer">
                                  {performer.participantName}
                                </p>
                              </Link>
                              <p className="text-sm text-muted-foreground">
                                {performer.eventsAttended} events attended
                              </p>
                            </div>
                          </div>
                          <Link href={`/analytics/${currentOrgId}/performer/${performer.participantId}?organisationId=${currentOrgId}&tab=performers&eventType=${selectedEventType || ''}&eventId=${selectedEventId || ''}&search=${performerSearchTerm}`}>
                            <Button variant="outline" size="sm">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Link>
                        </div>
                        <div className="pl-11">
                          <p className="font-semibold">{performer.totalPerformances} performances</p>
                          <p className="text-sm text-muted-foreground">
                            {formatMinutes(performer.totalStageTime)} total stage time
                          </p>
                        </div>
                      </div>
                    </div>
                  )) || (
                    <div className="text-center text-muted-foreground py-8">No performer data available</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        </div>
      </main>
    </PageBackground>
  );
}